// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/campaignRoute.test.ts
//
// Single source of truth for ask-puntt project workspace search params.
// Re-exported by ./campaignRoute; read as source text by rembrandt cross-layer
// contract tests (services/rembrandt/tests/test_review_agent.py +
// test_review_service_run.py), which assert chatId normalization so the FE token
// stays in lockstep with the review pipeline.
//
// URL contract for /ask-puntt/projects/:projectId:
//   ?tab=feed|chats     — project workspace tab (default feed, or chats when chatId set)
//   ?chatId=<id>        — personal chat in My Chats
//   ?review=<ticketId>  — in-project artwork review workspace
//   ?version=<n>        — optional version pin when review is open

export type CampaignTab = "feed" | "chats";

export type CampaignSearch = {
  tab?: CampaignTab;
  chatId?: string;
  review?: string;
  version?: number;
};

export type CampaignSearchInput = Partial<CampaignSearch>;

/** Raw search record from TanStack validateSearch. */
export function parseCampaignSearch(
  search: Record<string, unknown>,
): CampaignSearch {
  const tabRaw = search.tab;
  const tab: CampaignTab | undefined =
    tabRaw === "feed" || tabRaw === "chats" ? tabRaw : undefined;

  const chatIdRaw = typeof search.chatId === "string" ? search.chatId : "";
  const chatId = chatIdRaw.trim().length > 0 ? chatIdRaw.trim() : undefined;

  const reviewRaw = typeof search.review === "string" ? search.review : "";
  const review = reviewRaw.trim().length > 0 ? reviewRaw.trim() : undefined;

  let version: number | undefined;
  if (search.version != null && search.version !== "") {
    const parsed = Number(search.version);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      version = parsed;
    }
  }

  const resolvedTab: CampaignTab | undefined =
    tab ?? (chatId != null ? "chats" : review != null ? "feed" : undefined);

  return {
    tab: resolvedTab,
    chatId,
    review,
    version: review != null && version != null ? version : undefined,
  };
}

/**
 * Search params for `campaignRoute` navigations (required by TanStack after
 * validateSearch). Accepts a legacy string chatId or a partial search object.
 */
export function campaignSearch(
  input?: string | CampaignSearchInput,
): CampaignSearch {
  const raw: CampaignSearchInput =
    typeof input === "string" ? { chatId: input } : (input ?? {});

  const chatId = raw.chatId?.trim() ? raw.chatId.trim() : undefined;
  const review = raw.review?.trim() ? raw.review.trim() : undefined;
  const tab =
    raw.tab === "feed" || raw.tab === "chats"
      ? raw.tab
      : chatId != null
        ? "chats"
        : review != null
          ? "feed"
          : undefined;

  const version =
    raw.version != null && raw.version >= 0 ? raw.version : undefined;

  return {
    tab,
    chatId,
    review,
    version: review != null && version != null ? version : undefined,
  };
}
